import cs from "classnames";
import React from "react";
import ReactDOM from "react-dom";

import "./Tooltip.css";

export interface TooltipProps {
	placement?: "top" | "bottom" | "right";
	canWrap?: boolean;
	maxWidth?: number;
	topOffset?: number;
	leftOffset?: number;
	title: React.ReactNode;
	content: React.ReactElement<{
		onMouseOver?(event: React.MouseEvent): unknown;
		onMouseLeave?(event: React.MouseEvent): unknown;
	}>;
}

export const Tooltip: React.FunctionComponent<TooltipProps> = ({
	title,
	content,
	...props
}) => {
	if (title === null || title === "") {
		return content;
	}
	return <TooltipContent {...props} title={title} content={content} />;
};

export const TooltipContent: React.FunctionComponent<TooltipProps> = ({
	placement = "top",
	canWrap = false,
	maxWidth,
	topOffset = 0,
	leftOffset = 0,
	title,
	content
}) => {
	const portalElRef = React.useRef(document.createElement("div"));

	React.useEffect(() => {
		const root = document.getElementById("tooltip-root");
		if (root === null) {
			throw new Error();
		}
		const el = portalElRef.current;
		root.appendChild(el);
		return () => {
			el.remove();
		};
	}, []);

	const titleRef = React.useRef<HTMLDivElement>(null);

	const [isVisible, setVisible] = React.useState(false);
	const [top, setTop] = React.useState(0);
	const [left, setLeft] = React.useState(0);

	const isVisibleRef = React.useRef(isVisible);

	React.useEffect(() => {
		isVisibleRef.current = isVisible;
	}, [isVisible]);

	const onMouseOver = React.useCallback(
		(event: React.MouseEvent) => {
			if (isVisibleRef.current) {
				return;
			}

			const targetElement = event.currentTarget;
			const titleElement = titleRef.current;

			if (titleElement === null) {
				return;
			}

			setVisible(true);

			const targetRect = targetElement.getBoundingClientRect();
			const titleRect = titleElement.getBoundingClientRect();

			if (placement === "top") {
				setTop(targetRect.top - titleRect.height + topOffset - 5);
				setLeft(targetRect.left + leftOffset);
			} else if (placement === "bottom") {
				setTop(targetRect.bottom + topOffset + 5);
				setLeft(targetRect.left + leftOffset);
			} else if (placement === "right") {
				setTop(targetRect.top + topOffset);
				setLeft(targetRect.right + leftOffset + 5);
			}
		},
		[placement, topOffset, leftOffset]
	);

	const onMouseLeave = React.useCallback(() => {
		setVisible(false);
	}, []);

	return (
		<>
			{ReactDOM.createPortal(
				<div
					ref={titleRef}
					className={cs("tooltip", `tooltip--${placement}`, {
						"tooltip--visible": isVisible
					})}
					style={{
						top,
						left,
						whiteSpace: canWrap ? "normal" : "nowrap",
						maxWidth
					}}
				>
					{title}
				</div>,
				portalElRef.current as HTMLDivElement
			)}
			{React.cloneElement(content, { onMouseOver, onMouseLeave })}
		</>
	);
};
